import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

import config from '../../config/config';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

function ManagePendingTours() {
  const [pendingTours, setPendingTours] = useState([]);
  const [filteredTours, setFilteredTours] = useState([]);
  const [searchString, setSearchString] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchPendingTours();
  }, []);

  useEffect(() => {
    if (searchString !== '') {
      const filtered = pendingTours.filter(tour => 
        tour.name.toLowerCase().includes(searchString.toLowerCase()) ||
        tour.operator.toLowerCase().includes(searchString.toLowerCase())
      );
      setFilteredTours(filtered);
    } else {
      setFilteredTours(pendingTours);
    }
  }, [searchString, pendingTours]);

  const fetchPendingTours = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.apiUrl}/api/pendingTours/all`);
      const data = response.data;
      
      // Flatten the data structure to show individual tours
      const flattenedTours = [];
      data.forEach(listing => {
        if (listing.pending_tours && listing.pending_tours.length > 0) {
          listing.pending_tours.forEach(tour => {
            flattenedTours.push({
              listing_id: listing._id,
              name: listing.name,
              operator: listing.operator,
              url: tour.url,
              status: tour.status || 'pending' // Default to 'pending' if not set
            });
          });
        }
      });
      
      setPendingTours(flattenedTours);
      setFilteredTours(flattenedTours);
    } catch (error) {
      console.error('Error fetching pending tours:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch pending tours',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchString(e.target.value);
  };

  const handleApprove = (tour) => {
    setActionDialog({
      type: 'approve',
      tour: tour,
      message: `Are you sure you want to approve the tour for ${tour.name}?`
    });
  };

  const handleReject = (tour) => {
    setActionDialog({
      type: 'reject',
      tour: tour,
      message: `Are you sure you want to reject the tour for ${tour.name}?`
    });
  };

  const handleActionConfirm = async () => {
    const { type, tour } = actionDialog;

    try {
      await axios.post(`${config.apiUrl}/api/pendingTours/set-status`, {
        listing_id: tour.listing_id,
        url: tour.url,
        status: type === 'approve' ? 'approved' : 'rejected'
      });

      setSnackbar({
        open: true,
        message: `Tour ${type === 'approve' ? 'approved' : 'rejected'} successfully`,
        severity: 'success'
      });

      // Refresh the list
      await fetchPendingTours();
    } catch (error) {
      console.error('Error updating tour status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update tour status',
        severity: 'error'
      });
    } finally {
      setActionDialog(null);
    }
  };

  const handleOpenTour = (url) => {
    window.open(url, '_blank');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar style={{ backgroundColor: 'white' }} position="static">
          <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search listings or operators…"
                inputProps={{ 'aria-label': 'search' }}
                onChange={handleSearch}
                value={searchString}
              />
            </Search>
          </Toolbar>
        </AppBar>
      </Box>

      <TableContainer style={{ marginTop: '30px' }} component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="pending tours table">
          <TableHead>
            <TableRow>
              <TableCell>Listing Name</TableCell>
              <TableCell>Operator</TableCell>
              <TableCell>Tour URL</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTours.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {searchString ? 'No tours found matching your search.' : 'No pending tours found.'}
                </TableCell>
              </TableRow>
            ) : (
              filteredTours.map((tour, index) => (
                <TableRow
                  key={`${tour.listing_id}-${tour.url}-${index}`}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell>{tour.name}</TableCell>
                  <TableCell>{tour.operator}</TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '8px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tour.url}
                      </span>
                      <Tooltip title="Open tour in new tab">
                        <IconButton size="small" onClick={() => handleOpenTour(tour.url)}>
                          <LinkIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span style={{ 
                      color: tour.status === 'rejected' ? '#f44336' : '#4caf50',
                      fontWeight: 'bold'
                    }}>
                      {tour.status === 'rejected' ? 'Rejected' : 'Pending'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <TableOption
                        color="success"
                        name="Approve Tour"
                        onClick={() => handleApprove(tour)}
                        disabled={tour.status === 'rejected'}
                      >
                        <CheckIcon />
                      </TableOption>

                      <TableOption
                        color="error"
                        name="Reject Tour"
                        onClick={() => handleReject(tour)}
                        disabled={tour.status === 'rejected'}
                      >
                        <CloseIcon />
                      </TableOption>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialog !== null}
        onClose={() => setActionDialog(null)}
        aria-labelledby="action-dialog-title"
        aria-describedby="action-dialog-description"
      >
        <DialogTitle id="action-dialog-title">
          Confirm Action
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="action-dialog-description">
            {actionDialog?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancel</Button>
          <Button 
            onClick={handleActionConfirm} 
            autoFocus
            color={actionDialog?.type === 'approve' ? 'success' : 'error'}
            variant="contained"
          >
            {actionDialog?.type === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ManagePendingTours;

const TableOption = ({ children, name, color, onClick, disabled = false }) => {
  return (
    <div style={{ margin: '0px 10px', cursor: disabled ? 'not-allowed' : 'pointer' }} onClick={disabled ? undefined : onClick}>
      <Tooltip title={name}>
        <IconButton color={color} disabled={disabled}>
          {children}
        </IconButton>
      </Tooltip>
    </div>
  );
};
